import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  ApiFailure,
  type TrafficDimension,
  type TrafficFilter,
  type TrafficPoint,
  type TrafficResponse,
  type TrafficRow,
  type TrafficSummary,
} from '../api';
import type { VizColors } from '../theme';
import { Notice } from './Panels';
import { Icon } from './Icon';
import {
  CountryBadge,
  countryName,
  Delta,
  fmtDuration,
  fmtExact,
  fmtMoney,
  fmtNumber,
  fmtPercent,
  LoadingCard,
  NoWebsite,
  PageHeader,
  RangePicker,
  SitePicker,
  useDateRange,
  useWebsites,
} from './analytics/shared';

/**
 * The traffic dashboard — what Plausible and Fathom open on, plus the revenue
 * column DataFast adds to every row.
 *
 * Every block on the page is computed by the server from one set of visits in
 * one pass, so the headline, the chart and the tables cannot disagree. Click
 * any row to filter the whole page by it; filters live in the URL so a
 * filtered view can be bookmarked and shared.
 */

type Metric = 'visitors' | 'visits' | 'pageviews' | 'revenue';

const METRIC_LABEL: Record<Metric, string> = {
  visitors: 'Unique visitors',
  visits: 'Visits',
  pageviews: 'Pageviews',
  revenue: 'Revenue',
};

const DIMENSION_LABEL: Record<TrafficDimension, string> = {
  page: 'Page',
  entry_page: 'Entry page',
  exit_page: 'Exit page',
  source: 'Source',
  referrer: 'Referrer',
  channel: 'Channel',
  utm_source: 'UTM source',
  utm_medium: 'UTM medium',
  utm_campaign: 'UTM campaign',
  utm_term: 'UTM term',
  utm_content: 'UTM content',
  country: 'Country',
  city: 'City',
  device: 'Device',
  browser: 'Browser',
  os: 'Operating system',
  goal: 'Goal',
};

function filtersFromUrl(): TrafficFilter[] {
  try {
    const raw = new URLSearchParams(window.location.search).get('f');
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as TrafficFilter[];
    return Array.isArray(parsed) ? parsed.filter((f) => typeof f.dimension === 'string' && typeof f.value === 'string') : [];
  } catch {
    return [];
  }
}

function filtersToUrl(filters: TrafficFilter[]): void {
  const url = new URL(window.location.href);
  if (filters.length === 0) url.searchParams.delete('f');
  else url.searchParams.set('f', JSON.stringify(filters));
  window.history.replaceState(window.history.state, '', url.toString());
}

export function Traffic({
  colors,
  onNavigate,
}: {
  colors: VizColors;
  onNavigate: (page: 'websites' | 'sources' | 'realtime') => void;
}) {
  const sites = useWebsites();
  const { range, preset, setPreset, custom, setCustom, refresh } = useDateRange();
  const [filters, setFilters] = useState<TrafficFilter[]>(filtersFromUrl);
  const [data, setData] = useState<TrafficResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [metric, setMetric] = useState<Metric>('visitors');
  const [compare, setCompare] = useState(true);
  const [live, setLive] = useState<number | null>(null);
  const token = useRef(0);

  const load = useCallback(async () => {
    if (sites.selected === '') return;
    const mine = ++token.current;
    setLoading(true);
    setFailure(null);
    try {
      const response = await api.getTraffic({
        websiteId: sites.selected,
        start: range.start,
        end: range.end,
        filters,
        compare,
      });
      if (mine === token.current) setData(response);
    } catch (error) {
      if (mine === token.current) setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    } finally {
      if (mine === token.current) setLoading(false);
    }
  }, [sites.selected, range.start, range.end, filters, compare]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live visitor count in the header, refreshed every 20 seconds while visible.
  useEffect(() => {
    if (sites.selected === '') return;
    let stopped = false;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      api
        .getRealtime(sites.selected)
        .then((r) => !stopped && setLive(r.realtime.visitorsNow))
        .catch(() => undefined);
    };
    tick();
    const id = window.setInterval(tick, 20_000);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [sites.selected]);

  const addFilter = useCallback((dimension: TrafficDimension, value: string) => {
    setFilters((current) => {
      const next = [...current.filter((f) => f.dimension !== dimension), { dimension, value }];
      filtersToUrl(next);
      return next;
    });
  }, []);

  const removeFilter = useCallback((dimension: TrafficDimension) => {
    setFilters((current) => {
      const next = current.filter((f) => f.dimension !== dimension);
      filtersToUrl(next);
      return next;
    });
  }, []);

  if (sites.websites === null) {
    return (
      <div className="page">
        <LoadingCard />
      </div>
    );
  }
  if (sites.websites.length === 0) {
    return <NoWebsite what="your traffic" onSetup={() => onNavigate('websites')} onUpload={() => onNavigate('sources')} />;
  }

  const summary = data?.report.summary ?? null;
  const prev = data?.previous?.summary ?? null;
  const hasRevenue = (summary?.revenue ?? 0) > 0 || (prev?.revenue ?? 0) > 0;
  const noData = data !== null && data.report.eventsConsidered === 0 && filters.length === 0;

  return (
    <div className="page an-page">
      <PageHeader title="Traffic" subtitle="Visitors, where they came from, what they read, and what they paid.">
        <SitePicker websites={sites.websites} selected={sites.selected} onChange={sites.setSelected} />
        <button className="live-pill" onClick={() => onNavigate('realtime')} title="Open realtime">
          <span className={live !== null && live > 0 ? 'live-dot on' : 'live-dot'} aria-hidden />
          {live === null ? '…' : live} live
        </button>
        <RangePicker preset={preset} onPreset={setPreset} custom={custom} onCustom={setCustom} />
        <label className="toggle">
          <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
          <span>Compare</span>
        </label>
        <button className="ghost icon-button" onClick={refresh} title="Refresh" aria-label="Refresh">
          <Icon name="realtime" size={15} />
        </button>
      </PageHeader>

      {filters.length > 0 && (
        <div className="filter-chips" role="list" aria-label="Active filters">
          {filters.map((f) => (
            <span className="filter-chip" role="listitem" key={f.dimension}>
              <span className="filter-chip-dim">{DIMENSION_LABEL[f.dimension]}</span>
              <strong>{f.dimension === 'country' ? countryName(f.value) : f.value}</strong>
              <button className="chip-x" aria-label={`Remove ${DIMENSION_LABEL[f.dimension]} filter`} onClick={() => removeFilter(f.dimension)}>
                <Icon name="close" size={12} />
              </button>
            </span>
          ))}
          <button className="ghost tiny" onClick={() => { setFilters([]); filtersToUrl([]); }}>
            Clear all
          </button>
        </div>
      )}

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {noData && <InstallHint domain={data.website.domain} onSetup={() => onNavigate('websites')} />}

      {summary === null ? (
        <LoadingCard label="Crunching visits…" />
      ) : (
        <>
          <section className={loading ? 'kpi-strip is-loading' : 'kpi-strip'} aria-label="Headline metrics">
            <Kpi label="Unique visitors" value={fmtNumber(summary.visitors)} exact={fmtExact(summary.visitors)}
              current={summary.visitors} previous={prev?.visitors} selected={metric === 'visitors'} onSelect={() => setMetric('visitors')} />
            <Kpi label="Visits" value={fmtNumber(summary.visits)} exact={fmtExact(summary.visits)}
              current={summary.visits} previous={prev?.visits} selected={metric === 'visits'} onSelect={() => setMetric('visits')} />
            <Kpi label="Pageviews" value={fmtNumber(summary.pageviews)} exact={fmtExact(summary.pageviews)}
              current={summary.pageviews} previous={prev?.pageviews} selected={metric === 'pageviews'} onSelect={() => setMetric('pageviews')} />
            <Kpi label="Views / visit" value={summary.viewsPerVisit === null ? '—' : summary.viewsPerVisit.toFixed(2)}
              current={summary.viewsPerVisit} previous={prev?.viewsPerVisit} />
            <Kpi label="Bounce rate" value={fmtPercent(summary.bounceRate, 0)} current={summary.bounceRate}
              previous={prev?.bounceRate} better="down" mode="points" />
            <Kpi label="Visit duration" value={fmtDuration(summary.avgVisitDurationMs)}
              current={summary.avgVisitDurationMs} previous={prev?.avgVisitDurationMs} />
            <Kpi label="Revenue" value={fmtMoney(summary.revenue, { compact: true })} exact={fmtMoney(summary.revenue)}
              current={summary.revenue} previous={prev?.revenue} selected={metric === 'revenue'} onSelect={() => setMetric('revenue')} accent />
            <Kpi label="Conversion rate" value={fmtPercent(summary.conversionRate)} current={summary.conversionRate}
              previous={prev?.conversionRate} mode="points" />
            <Kpi label="Revenue / visitor" value={fmtMoney(summary.revenuePerVisitor)} current={summary.revenuePerVisitor}
              previous={prev?.revenuePerVisitor} />
          </section>

          <section className="card chart-card">
            <header>
              <h2>{METRIC_LABEL[metric]}</h2>
              <span className="sub">
                by {data!.report.interval} · {compare && data!.previous !== null ? 'dashed line is the previous period' : 'no comparison'}
              </span>
            </header>
            <TrafficChart
              series={data!.report.series}
              previous={compare ? (data!.previous?.series ?? null) : null}
              metric={metric}
              interval={data!.report.interval}
              colors={colors}
            />
          </section>

          <div className="panel-grid">
            <BreakdownPanel
              title="Sources"
              icon="link"
              tabs={[
                ['channel', 'Channels'],
                ['source', 'Sources'],
                ['referrer', 'Referrers'],
                ['utm_campaign', 'Campaigns'],
                ['utm_medium', 'Medium'],
                ['utm_content', 'Content'],
                ['utm_term', 'Term'],
              ]}
              data={data!}
              hasRevenue={hasRevenue}
              onFilter={addFilter}
              filters={filters}
            />
            <BreakdownPanel
              title="Pages"
              icon="page"
              tabs={[
                ['page', 'Top pages'],
                ['entry_page', 'Entry pages'],
                ['exit_page', 'Exit pages'],
              ]}
              data={data!}
              hasRevenue={hasRevenue}
              onFilter={addFilter}
              filters={filters}
            />
            <BreakdownPanel
              title="Locations"
              icon="globe"
              tabs={[
                ['country', 'Countries'],
                ['city', 'Cities'],
              ]}
              data={data!}
              hasRevenue={hasRevenue}
              onFilter={addFilter}
              filters={filters}
            />
            <BreakdownPanel
              title="Devices"
              icon="device"
              tabs={[
                ['device', 'Devices'],
                ['browser', 'Browsers'],
                ['os', 'OS'],
              ]}
              data={data!}
              hasRevenue={hasRevenue}
              onFilter={addFilter}
              filters={filters}
            />
          </div>

          <GoalsPanel rows={data!.report.breakdowns.goal} totalVisitors={summary.visitors} onFilter={addFilter} />

          <p className="an-footnote">
            <Icon name="info" size={13} /> Visitors are counted by a daily-rotating, salted hash — no cookies, no IPs stored.
            A bounce is a visit with one pageview and no goal. Duration is visible time on page.
            {data!.revenueScope === 'workspace'
              ? ' Revenue in the headline is every payment in the period; revenue in a row is credited to the visit before each payment (last touch, 30 days).'
              : ' This workspace has several sites, so revenue shown here is only what was credited to visits on this site.'}
            {data!.unattributedRevenue > 0 && ` ${fmtMoney(data!.unattributedRevenue)} could not be matched to a visit — pass DataVantage.visitorId() through checkout to close the gap.`}
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Kpi({
  label,
  value,
  exact,
  current,
  previous,
  better = 'up',
  mode = 'relative',
  selected = false,
  onSelect,
  accent = false,
}: {
  label: string;
  value: string;
  exact?: string;
  current: number | null | undefined;
  previous: number | null | undefined;
  better?: 'up' | 'down';
  mode?: 'relative' | 'points';
  selected?: boolean;
  onSelect?: () => void;
  accent?: boolean;
}) {
  const body = (
    <>
      <span className="kpi-label">{label}</span>
      <span className={accent ? 'kpi-value accent' : 'kpi-value'} title={exact}>
        {value}
      </span>
      {previous !== undefined && <Delta current={current} previous={previous} better={better} mode={mode} />}
    </>
  );
  if (onSelect === undefined) return <div className="kpi">{body}</div>;
  return (
    <button className={selected ? 'kpi selectable selected' : 'kpi selectable'} aria-pressed={selected} onClick={onSelect}>
      {body}
    </button>
  );
}

function formatBucket(iso: string, interval: 'hour' | 'day' | 'month'): string {
  const d = new Date(iso);
  if (interval === 'hour') return d.toLocaleTimeString('en-US', { hour: 'numeric' });
  if (interval === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function TrafficChart({
  series,
  previous,
  metric,
  interval,
  colors,
}: {
  series: TrafficPoint[];
  previous: TrafficPoint[] | null;
  metric: Metric;
  interval: 'hour' | 'day' | 'month';
  colors: VizColors;
}) {
  // The previous period is aligned by position: day 1 against day 1.
  const rows = useMemo(
    () =>
      series.map((point, i) => ({
        label: formatBucket(point.bucket, interval),
        bucket: point.bucket,
        current: point[metric],
        previous: previous?.[i]?.[metric] ?? null,
        previousBucket: previous?.[i]?.bucket ?? null,
      })),
    [series, previous, metric, interval],
  );
  const money = metric === 'revenue';
  const fmt = (v: number) => (money ? fmtMoney(v, { compact: true }) : fmtNumber(v));
  const total = rows.reduce((s, r) => s + (r.current ?? 0), 0);

  if (total === 0 && (previous === null || rows.every((r) => !r.previous))) {
    return <div className="chart-empty">No {METRIC_LABEL[metric].toLowerCase()} in this period yet.</div>;
  }

  return (
    <div className="chart-frame" style={{ height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="trafficFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.series1} stopOpacity={0.22} />
              <stop offset="100%" stopColor={colors.series1} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={colors.grid} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: colors.muted }} tickLine={false} axisLine={{ stroke: colors.axis }} minTickGap={24} />
          <YAxis tick={{ fontSize: 12, fill: colors.muted }} tickLine={false} axisLine={false} width={56} tickFormatter={fmt} allowDecimals={false} />
          <Tooltip
            cursor={{ stroke: colors.axis, strokeWidth: 1 }}
            content={({ active, payload }) => {
              if (active !== true || payload === undefined || payload.length === 0) return null;
              const row = payload[0]!.payload as (typeof rows)[number];
              return (
                <div className="chart-tooltip">
                  <div className="tt-row">
                    <span className="tt-swatch" style={{ background: colors.series1 }} />
                    <span>{new Date(row.bucket).toLocaleString('en-US', interval === 'hour' ? { month: 'short', day: 'numeric', hour: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <strong>{money ? fmtMoney(row.current) : fmtExact(row.current)}</strong>
                  </div>
                  {row.previous !== null && row.previousBucket !== null && (
                    <div className="tt-row muted">
                      <span className="tt-swatch dashed" style={{ borderColor: colors.muted }} />
                      <span>{new Date(row.previousBucket).toLocaleString('en-US', interval === 'hour' ? { month: 'short', day: 'numeric', hour: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <strong>{money ? fmtMoney(row.previous) : fmtExact(row.previous)}</strong>
                    </div>
                  )}
                </div>
              );
            }}
          />
          {previous !== null && <Legend verticalAlign="top" align="right" height={24} iconType="plainline" wrapperStyle={{ fontSize: 12 }} />}
          <Area
            type="monotone"
            dataKey="current"
            name="This period"
            stroke={colors.series1}
            strokeWidth={2}
            fill="url(#trafficFill)"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: colors.surface }}
            isAnimationActive={false}
          />
          {previous !== null && (
            <Line
              type="monotone"
              dataKey="previous"
              name="Previous period"
              stroke={colors.muted}
              strokeWidth={1.5}
              strokeDasharray="4 4"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function rowLabel(dimension: TrafficDimension, key: string): string {
  if (dimension === 'country') return key === 'Unknown' ? 'Unknown' : countryName(key);
  if (dimension === 'device' || dimension === 'channel') return key.charAt(0).toUpperCase() + key.slice(1);
  return key;
}

/**
 * A letter badge, not a fetched favicon: loading icons from a third-party
 * service would tell it which referrers every customer gets.
 */
function Favicon({ domain }: { domain: string }) {
  const letter = /^[a-z0-9]/i.test(domain) ? domain.charAt(0).toUpperCase() : '·';
  return (
    <span className="row-favicon" aria-hidden>
      {letter}
    </span>
  );
}

function BreakdownPanel({
  title,
  icon,
  tabs,
  data,
  hasRevenue,
  onFilter,
  filters,
}: {
  title: string;
  icon: string;
  tabs: [TrafficDimension, string][];
  data: TrafficResponse;
  hasRevenue: boolean;
  onFilter: (dimension: TrafficDimension, value: string) => void;
  filters: TrafficFilter[];
}) {
  const storageKey = `dv-tab-${title}`;
  const [tab, setTab] = useState<TrafficDimension>(() => {
    try {
      const stored = localStorage.getItem(storageKey) as TrafficDimension | null;
      if (stored !== null && tabs.some(([d]) => d === stored)) return stored;
    } catch {
      /* ignore */
    }
    return tabs[0]![0];
  });
  const [metric, setMetric] = useState<'visitors' | 'revenue'>('visitors');
  const [expanded, setExpanded] = useState(false);
  const rows = data.report.breakdowns[tab] ?? [];
  const shown = expanded ? rows : rows.slice(0, 9);
  const valueOf = (r: TrafficRow) => (metric === 'revenue' ? r.revenue : tab === 'page' ? r.pageviews : r.visitors);
  const max = Math.max(1, ...rows.map(valueOf));
  const active = new Set(filters.map((f) => f.dimension));

  return (
    <section className="card breakdown">
      <header className="breakdown-head">
        <h2>
          <Icon name={icon} size={15} /> {title}
        </h2>
        <div className="seg" role="tablist" aria-label={`${title} breakdown`}>
          {tabs.map(([dimension, label]) => (
            <button
              key={dimension}
              role="tab"
              aria-selected={tab === dimension}
              className={tab === dimension ? 'seg-item on' : 'seg-item'}
              onClick={() => {
                setTab(dimension);
                setExpanded(false);
                try {
                  localStorage.setItem(storageKey, dimension);
                } catch {
                  /* ignore */
                }
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="breakdown-cols">
        <span>{tabs.find(([d]) => d === tab)?.[1]}</span>
        <span className="breakdown-metric-switch">
          <button className={metric === 'visitors' ? 'on' : ''} onClick={() => setMetric('visitors')}>
            {tab === 'page' ? 'Views' : 'Visitors'}
          </button>
          {hasRevenue && (
            <button className={metric === 'revenue' ? 'on' : ''} onClick={() => setMetric('revenue')}>
              Revenue
            </button>
          )}
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="breakdown-empty">
          {tab.startsWith('utm_')
            ? 'No visits carried this UTM tag in the period.'
            : tab === 'city'
              ? 'City needs a geo header from your CDN (Cloudflare, Vercel, CloudFront).'
              : 'Nothing recorded for this period.'}
        </div>
      ) : (
        <ol className="breakdown-list">
          {shown.map((row) => {
            const text = rowLabel(tab, row.key);
            const value = valueOf(row);
            return (
              <li key={row.key}>
                <button
                  className="breakdown-row"
                  onClick={() => onFilter(tab, row.key)}
                  disabled={active.has(tab)}
                  title={`Filter by ${text}`}
                >
                  <span className="breakdown-bar" style={{ width: `${(value / max) * 100}%` }} aria-hidden />
                  <span className="breakdown-key">
                    {(tab === 'source' || tab === 'referrer') && <Favicon domain={row.key} />}
                    {tab === 'country' && <CountryBadge code={row.key} />}
                    <span className="breakdown-text">{text}</span>
                  </span>
                  <span className="breakdown-value">
                    {metric === 'revenue' ? fmtMoney(row.revenue, { compact: true }) : fmtNumber(value)}
                    {metric === 'visitors' && row.share !== null && tab !== 'page' && (
                      <span className="breakdown-share">{row.share.toFixed(0)}%</span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
      {rows.length > 9 && (
        <button className="ghost tiny breakdown-more" onClick={() => setExpanded(!expanded)}>
          {expanded ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </section>
  );
}

function GoalsPanel({
  rows,
  totalVisitors,
  onFilter,
}: {
  rows: TrafficRow[];
  totalVisitors: number;
  onFilter: (dimension: TrafficDimension, value: string) => void;
}) {
  return (
    <section className="card">
      <header>
        <h2>
          <Icon name="goal" size={15} /> Goal conversions
        </h2>
        <span className="sub">custom events, outbound clicks and file downloads</span>
      </header>
      {rows.length === 0 ? (
        <div className="goal-empty">
          <p>
            No goals yet. Track signups, checkouts or any action with one line —{' '}
            <code>DataVantage.track('Signup', {'{ plan: "team" }'})</code>. Outbound link clicks and file downloads are
            tracked automatically.
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table goal-table">
            <thead>
              <tr>
                <th>Goal</th>
                <th className="num">Uniques</th>
                <th className="num">Total</th>
                <th className="num">Conversion rate</th>
                <th className="num">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} onClick={() => onFilter('goal', row.key)} className="clickable" title={`Filter by ${row.key}`}>
                  <td>
                    <strong>{row.key}</strong>
                  </td>
                  <td className="num">{fmtExact(row.visitors)}</td>
                  <td className="num">{fmtExact(row.events)}</td>
                  <td className="num">
                    <span className="inline-meter" aria-hidden>
                      <span style={{ width: `${Math.min(100, row.conversionRate ?? 0)}%` }} />
                    </span>
                    {totalVisitors === 0 ? '—' : fmtPercent(row.conversionRate)}
                  </td>
                  <td className="num">{row.revenue > 0 ? fmtMoney(row.revenue) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function InstallHint({ domain, onSetup }: { domain: string; onSetup: () => void }) {
  return (
    <section className="install-hint">
      <div>
        <strong>Waiting for the first visit to {domain}</strong>
        <p>
          Nothing has been recorded in this period. If you have just installed the snippet, open your site in another
          tab — the visit appears on the Realtime page within seconds.
        </p>
      </div>
      <button className="primary" onClick={onSetup}>
        Get the snippet
      </button>
    </section>
  );
}

export type { TrafficSummary };
